"use client";

import { AnimatedList } from "@workspace/ui/components/animated-list";
import { Icons } from "@workspace/ui/components/icons";

import { TurboLink } from "~/components/common/turbo-link";
import { DISCORD_URL } from "~/lib/constants/urls";
import { useBreakpoint } from "~/lib/hooks/use-media-query";

const NOTIFICATIONS = [
  {
    title: "Setup & Publish",
    description: "Time saved - 5h",
    time: "30m ago",
    icon: Icons.Rocket,
    href: "/docs/web/deployment/checklist",
  },
  {
    title: "Community Support",
    description: "Time saved - 8h",
    time: "23m ago",
    icon: Icons.UserGroup,
    href: DISCORD_URL,
  },
  {
    title: "25+ SaaS Ideas",
    description: "Time saved - 10h",
    time: "15m ago",
    icon: Icons.LightBulb,
    href: "/docs/web/extras",
  },
  {
    title: "SEO & Marketing",
    description: "Time saved - 17h",
    time: "8m ago",
    icon: Icons.Marketing,
    href: "/docs/web/marketing/seo",
  },
  {
    title: "Lifetime Updates",
    description: "Time saved - Infinity",
    time: "3m ago",
    icon: Icons.Update,
    href: "/#pricing",
  },
];

export const Mobile = () => {
  const isDesktop = useBreakpoint("lg");
  const notifications = isDesktop ? NOTIFICATIONS : NOTIFICATIONS.slice(1);

  return (
    <div className="flex w-full flex-col items-center gap-3 pt-2">
      <div className="-mt-4 ml-16 grid grid-cols-2 grid-rows-2 gap-x-2 md:mt-0 md:gap-x-3 lg:mt-3">
        <div className="col-start-1 row-start-2 justify-self-end">
          <svg
            className="w-8 sm:w-11 md:w-14 lg:w-16"
            viewBox="0 0 62 48"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M61.8754 0.551145C54.8503 -1.84165 36.219 4.25727 26.2904 11.9612C15.5409 20.2636 11.0877 26.7932 5.10856 43.2283C4.51637 42.3374 4.10998 41.5573 3.62858 40.9263C3.11032 40.1839 2.555 39.4044 1.88719 38.8116C1.7018 38.6262 1.03187 38.6287 0.770639 38.8158C0.509409 39.0029 0.395423 39.6359 0.506215 39.8959C1.6135 42.3481 2.79557 44.7256 3.97723 47.1775C4.3838 47.9575 5.05291 48.1783 5.87327 47.7287C8.89472 45.8937 11.9533 44.0958 14.9749 42.1865C15.6465 41.7373 16.1707 40.9166 16.9177 40.1695C14.7257 39.1728 13.5662 40.7031 12.1871 41.2665C10.7335 41.8303 9.39067 42.654 7.63828 43.5165C8.66258 38.3395 10.5028 33.9038 13.0117 29.7634C22.1496 14.5447 35.8781 5.85887 52.9741 2.25925C55.2088 1.80428 57.4798 1.6097 59.7142 1.22915C60.3105 1.00361 60.8695 0.815441 61.8754 0.551145Z"
              className="fill-foreground/30"
            />
          </svg>
        </div>
        <div className="relative z-10 col-start-2 row-start-1 -mb-2 flex items-center justify-center gap-2 md:gap-2.5">
          <Icons.Phone className="xs:size-6 size-5 sm:size-7 md:size-8 lg:size-9" />
          <span className="text-lg leading-9 font-medium sm:text-xl md:text-2xl lg:text-[27px]">
            Mobile
          </span>
        </div>
      </div>

      <div className="border-border bg-background relative flex h-[25rem] w-full max-w-[23rem] flex-col items-center gap-4 rounded-t-[4rem] border-[7px] border-b-0 p-4 sm:rounded-t-[5rem] sm:border-[8px] sm:border-b-0 md:h-[28rem] md:gap-5 lg:h-[37rem] lg:max-w-[28rem] lg:gap-6 lg:border-[9px] lg:border-b-0 lg:p-5">
        <div className="bg-border absolute top-[20%] -left-[3px] h-[8%] w-2 -translate-x-full rounded-full lg:w-3" />
        <div className="bg-border absolute top-[35%] -left-[3px] h-[12%] w-2 -translate-x-full rounded-full lg:w-3" />
        <div className="bg-border absolute top-[49%] -left-[3px] h-[12%] w-2 -translate-x-full rounded-full lg:w-3" />

        <div className="bg-border absolute top-[35%] -right-[3px] h-[20%] w-2 translate-x-full rounded-full lg:w-3" />
        <div className="bg-border h-7 w-2/5 max-w-26 shrink-0 rounded-full sm:h-8 md:h-7 md:max-w-32 lg:h-9 lg:max-w-36 xl:h-10" />

        <div className="w-full px-1 sm:px-2 lg:px-3">
          <AnimatedList className="gap-3 lg:gap-4">
            {notifications.map((notification, id) => (
              <TurboLink
                rel="noopener noreferrer"
                href={notification.href}
                target="_blank"
                key={id}
                className="bg-card flex h-16 w-full items-center justify-start gap-2 rounded-2xl border p-3 shadow-[0px_0px_0px_1px_rgba(0,0,0,0.03),0px_12px_24px_0px_rgba(0,0,0,0.05),0px_4px_6px_-1px_rgba(243,244,246,0.70)] transition-transform duration-150 ease-out will-change-transform hover:scale-[1.02] hover:cursor-pointer active:hover:scale-[0.97] sm:gap-3 md:gap-2 lg:h-20 lg:gap-3 lg:p-4 dark:shadow-[0px_0px_0px_1.17px_rgba(0,0,0,0.03),0px_14px_28px_0px_rgba(0,0,0,0.05)]"
              >
                <div className="bg-secondary/50 dark:bg-secondary-foreground/20 flex size-10 shrink-0 items-center justify-center rounded-full sm:size-12 md:size-10 lg:size-12">
                  <notification.icon className="text-primary w-6 sm:w-7 md:w-6 lg:w-7" />
                </div>

                <div className="flex flex-col gap-px">
                  <div className="flex items-center gap-1 leading-tight">
                    <span className="text-sm font-medium sm:text-base md:text-sm lg:text-base">
                      {notification.title}
                    </span>
                    <span className="font-medium">·</span>
                    <span className="text-muted-foreground text-xs font-medium sm:text-sm md:text-xs lg:text-sm">
                      {notification.time}
                    </span>
                  </div>
                  <span className="text-muted-foreground text-xs font-medium sm:text-sm">
                    {notification.description}
                  </span>
                </div>
              </TurboLink>
            ))}
          </AnimatedList>
        </div>
      </div>
    </div>
  );
};
